import { slicedToArray as _slicedToArray, defineProperty as _defineProperty, objectWithoutProperties as _objectWithoutProperties, objectSpread2 as _objectSpread2, createClass as _createClass, classCallCheck as _classCallCheck, typeof as _typeof } from '../_virtual/_rollupPluginBabelHelpers.js';
import { MENUS, MENU_SLUG, PERMITTED_WHITELIST } from '../constants/menu.js';
import { i18n } from '../i18n/translate.js';
var _excluded = ["url"],
    _excluded2 = ["slug", "environment", "currentUrl"];
var commonRolesPerCategory = {
  executive: ['executive'],
  engage_managers: ['event_manager', 'event_staff'],
  next_managers: ['at_work_manager', 'at_work_advisor', 'pii_restricted_next_primary', 'pii_restricted_next_advisor'],
  next_main_managers: ['at_work_manager', 'pii_restricted_next_primary'],
  advisors: ['at_work_advisor', 'pii_restricted_next_advisor'],
  k12: ['administrator_executive', 'administrator_curriculum_coordinator', 'instructor_teacher', 'instructor_aide'],
  he_cc_fs_learner_names: ['he_learner', 'cc_learner', 'fac_staff_learner'],
  he_cc_fs_manager_names: ['he_admin', 'cc_admin', 'fac_staff_admin'],
  he_cc_fs_scorm_manager_names: ['he_admin', 'cc_admin', 'fac_staff_admin', 'scorm_manager'],
  he_cc_fs_primary_managers: ['he_admin', 'cc_admin', 'fac_staff_admin']
};
var COMMON_VALS = Object.entries(commonRolesPerCategory).map(function (_ref) {
  var _ref2 = _slicedToArray(_ref, 2),
      key = _ref2[0],
      value = _ref2[1];

  return _defineProperty({}, key, value);
});
var menuRoles = {
  impact: commonRolesPerCategory.executive,
  engage: commonRolesPerCategory.engage_managers,
  promote: commonRolesPerCategory.next_managers,
  learn: commonRolesPerCategory.he_cc_fs_learner_names,
  train: commonRolesPerCategory.he_cc_fs_scorm_manager_names
};
/*
  Some menu items have permissions to filter who should see it. If it's not there, everyone can see it.
  permission conditionals:
    has_capabilities, not_capabilities
    has_rule_set_role, not_rule_set_role
    has_rule_set, not_rule_set

  The && and || conditions in the menu entries are handled by the following structure:
    {
      has_capabilities: ['Capabilities::Something'], << OR
      has_rule_set: ['advisors', { 'engage_managers': 'event_manager' }]
    },{ << OR
      has_capabilities: ['Capabilities::Something'],
      has_rule_set_role: ['he_cc_fs_primary_managers']
    },{ << OR
      has_capabilities: ['Capabilities::Something', 'Capabilities::SomethingElse'],
      has_rule_set_role: ['next_managers', 'he_cc_fs_primary_managers']              ^ AND
    }
  ]
  TO put it another way:
  A conditional with ||
  [ { 'this' }, { 'or this' } ]
  A conditional with &&
  [ { 'this', 'and this' } , << OR { 'other stuff' } ]
  Combine these different ways to create complex conditions


  NOTE: When adding permissions to a menu object, has_capabilities: [] must come first in the order.

  Order is important when setting up the rest of the permissions as well. It
  works in the same way as an if statement with regard to order, with the first entry in the
  permissions object being the first item whose condition is checked. && and || setups work
  with the same "order of operations" one would expect when creating an "if" set.
  i. e.
  if( (capabilites === 'Capabilities::Something' || capabilities === 'Capabilities::SomethingElse') && role === next_managers.at_work_manager )

  has_capabilities will almost always be an && before moving on to whatever else we want to check. If it's missing, then it's assumed all capabilities will be allowed.

  If you have more than one Capabilities::whatever in the array, then it'll do || between them, then move on with &&.

  The reason we check has_capabilites first; if they don't have it, there's no need to check the rest of that block.
  However, you can chain blocks with different capabilites, each object block starting with its own has_capabilities.
*/

var PermissionManager = /*#__PURE__*/_createClass(function PermissionManager(user, organization, currentRuleSetName) {
  var _this = this;

  _classCallCheck(this, PermissionManager);

  _defineProperty(this, "adminHasPermission", function (value) {
    var _this$user, _this$user2;

    if ((_this$user = _this.user) !== null && _this$user !== void 0 && _this$user.all_access) {
      return true;
    } else if ((_this$user2 = _this.user) !== null && _this$user2 !== void 0 && _this$user2.permissions && Array.isArray(_this.user.permissions)) {
      return _this.user.permissions.some(function (permission) {
        return value.includes(permission);
      });
    }

    return false;
  });

  _defineProperty(this, "hasRuleSetRoleNameMatch", function (ruleSetRoles, type, name) {
    return ruleSetRoles.some(function (rule) {
      return type === 'has_rule_set_role' // headerMenu doesn't have currentRuleSetName, sidebarMenu does
      ? rule.rule_set === name && (!_this.currentRuleSetName || _this.currentRuleSetName === name) && rule.role === 'primary' : rule.rule_set !== name && rule.role !== 'primary';
    });
  });

  _defineProperty(this, "hasRuleSetNameMatch", function (ruleSets, name, isAppSwitcher) {
    // for App Switcher, we don't want to hide non-matching options, in order to be available to change rule set
    var selectedRuleSetMatches = isAppSwitcher || !_this.currentRuleSetName || _this.currentRuleSetName === name;
    return ruleSets.some(function (item) {
      return item.name === name && selectedRuleSetMatches;
    });
  });

  _defineProperty(this, "hasNameMatch", function (type, name, isAppSwitcher) {
    if (!_this.user) return false;
    var _this$user3 = _this.user,
        permissions = _this$user3.permissions,
        rule_sets = _this$user3.rule_sets,
        user_rule_set_roles = _this$user3.user_rule_set_roles;

    if (user_rule_set_roles && (type === 'has_rule_set_role' || type === 'not_rule_set_role')) {
      return _this.hasRuleSetRoleNameMatch(user_rule_set_roles, type, name);
    }

    if (rule_sets && (type === 'has_rule_set' || type === 'not_rule_set')) {
      return _this.hasRuleSetNameMatch(rule_sets, name, isAppSwitcher);
    }

    if (permissions) {
      return _this.adminHasPermission(name);
    }
  });

  _defineProperty(this, "searchCommonVals", function (type, key, value, isAppSwitcher) {
    // value is an optional restrictive search against values per key in COMMON_VALS
    var checked = false;
    COMMON_VALS.forEach(function (entry) {
      if (entry[key]) {
        if (value) {
          checked = _this.hasNameMatch(type, value, isAppSwitcher);
        } else {
          checked = entry[key].some(function (name) {
            return _this.hasNameMatch(type, name, isAppSwitcher);
          });
        }
      }
    });
    return checked;
  });

  _defineProperty(this, "hasCapability", function (value) {
    if (!_this.organization) return false;
    return value.some(function (val) {
      var _this$organization;

      return (_this$organization = _this.organization) === null || _this$organization === void 0 ? void 0 : _this$organization.capability_list.includes(val);
    });
  });

  _defineProperty(this, "hasSetOrRole", function (key, value, isAppSwitcher) {
    value = Array.isArray(value) ? value : [value];
    var access = false;
    value.every(function (val) {
      // val is a key in COMMON_VALS
      if ((key === 'has_rule_set' || key === 'not_rule_set') && val === 'event_manager_not_cra_content_only') {
        access = _this.searchCommonVals(key, 'engage_managers', 'event_manager', isAppSwitcher);
      } else if ((key === 'has_rule_set' || key === 'not_rule_set') && _typeof(val) === 'object') {
        // used for restricting to one value per key in COMMON_VALS search. i. e. { 'next_managers': 'at_work_manager' }
        access = _this.searchCommonVals(key, Object.keys(val)[0], val[Object.keys(val)[0]], isAppSwitcher);
      } else if (key !== 'url' || key !== 'path_includes') {
        // catch has_ & not_ rule_set_role/rule_set coupled with a key for COMMON_VALS
        access = _this.searchCommonVals(key, val, null, isAppSwitcher);
      }

      return !access;
    });
    return access;
  });

  _defineProperty(this, "processRulesConditionals", function (key, value, isAppSwitcher) {
    var hasAccess = _this.hasSetOrRole(key, value, isAppSwitcher);

    if (key === 'not_rule_set' || key === 'not_rule_set_role' || key === 'not_capabilities') {
      return !hasAccess;
    }

    return hasAccess;
  });

  _defineProperty(this, "processCapabilitiesConditionals", function (key, value) {
    var hasAccess = _this.hasCapability(value);

    if (key === 'not_capabilities') {
      return !hasAccess;
    }

    return hasAccess;
  });

  _defineProperty(this, "hasAccess", function (permissions, isAppSwitcher) {
    var hasAccess = true; // When we loop, we first do the && pass, and if false, we do another loop on permissions.every = an || pass.

    permissions.every(function (permission) {
      hasAccess = true; // set it to true for ||

      Object.entries(permission).forEach(function (_ref4) {
        var _this$user4;

        var _ref5 = _slicedToArray(_ref4, 2),
            key = _ref5[0],
            value = _ref5[1];

        if (key === 'has_capabilities' || key === 'not_capabilities') hasAccess = _this.processCapabilitiesConditionals(key, value);
        if (hasAccess && key === 'has_rule_set_role' || hasAccess && key === 'not_rule_set_role') hasAccess = _this.processRulesConditionals(key, value, isAppSwitcher);
        if (hasAccess && key === 'has_rule_set' || hasAccess && key === 'not_rule_set') hasAccess = _this.processRulesConditionals(key, value, isAppSwitcher);
        if (hasAccess && key === 'path_includes') hasAccess = typeof window !== 'undefined' && window.location.href.indexOf(value) !== -1;
        if (hasAccess && key === 'user_all_access') hasAccess = (_this$user4 = _this.user) === null || _this$user4 === void 0 ? void 0 : _this$user4.all_access;
        if (hasAccess && key === 'admin_has_permission') hasAccess = _this.adminHasPermission(value);
      }); // break out of the loop if they have access

      return !hasAccess;
    });
    return hasAccess;
  });

  _defineProperty(this, "isAdvisor", function () {
    var _this$user5;

    return (_this$user5 = _this.user) !== null && _this$user5 !== void 0 && _this$user5.rule_sets ? _this.user.rule_sets.some(function (rule) {
      var _rule$role;

      return (_rule$role = rule.role) === null || _rule$role === void 0 ? void 0 : _rule$role.includes('advisor');
    }) : false;
  });

  this.user = user;
  this.organization = organization;
  this.currentRuleSetName = currentRuleSetName;
});

var replaceSchoolName = function replaceSchoolName(user, title) {
  if (user && title.includes('{{current_school.name}}')) {
    var _user$current_school;

    return title.replace('{{current_school.name}}', ((_user$current_school = user.current_school) === null || _user$current_school === void 0 ? void 0 : _user$current_school.name) || i18n.t('Choose a School'));
  }

  return title;
};

var replaceSchoolModalUrl = function replaceSchoolModalUrl(user, target_url) {
  if (user && target_url.includes('school_modal')) {
    return target_url.replace('school_modal', '');
  }

  return target_url;
};

var replaceUserName = function replaceUserName(user, title) {
  if (user && title.includes('{{user_name}}')) {
    return title.replace('{{user_name}}', user.full_name || "".concat(user.first_name, " ").concat(user.last_name));
  }

  return title;
};

var replaceAccountManagerEmailText = function replaceAccountManagerEmailText(user, sub_text) {
  if (user !== null && user !== void 0 && user.account_manager_email && sub_text) {
    return sub_text.replace('{{account_manager_email}}', user.account_manager_email);
  }

  return sub_text;
};

var replaceAccountManagerEmailUrl = function replaceAccountManagerEmailUrl(user, target_url) {
  if (user !== null && user !== void 0 && user.account_manager_email && target_url) {
    return target_url.replace('{{account_manager_email}}', user.account_manager_email);
  }

  return target_url;
};

var replaceHelpUrl = function replaceHelpUrl(menu, organization, isAdvisor) {
  if (isAdvisor && organization !== null && organization !== void 0 && organization.advisor_resource_url) {
    return organization.advisor_resource_url;
  } else if (menu !== null && menu !== void 0 && menu.foundryKnowledgeBaseUrl) {
    // foundryKnowledgeBaseUrl is ENV.FOUNDARY_KNOWLEDGE_BASE_URL if was added to this menu item
    return menu.foundryKnowledgeBaseUrl;
  } // the fallback is whatever the target_url is already set to in the menu file


  return menu.target_url;
};

var replaceMyProfileUrlContext = function replaceMyProfileUrlContext(targetUrl, isManager) {
  return targetUrl.replace('{{context}}', isManager ? 'cportal' : 'learner');
};

var replaceOrgSlug = function replaceOrgSlug(menu, organization, user) {
  var orgSlug = organization ? organization.slug : user === null || user === void 0 ? void 0 : user.organization_slug;

  if (orgSlug && menu.target_url.includes('{{slug}}')) {
    return menu.target_url.replace('{{slug}}', orgSlug);
  }

  return menu.target_url;
};

var replacePrivacyUrl = function replacePrivacyUrl(menu, organization) {
  var privacyType = "privacy_".concat(menu.privacy_processing, "_url");
  menu.target_url = organization && organization[privacyType] ? organization[privacyType] : '';
  return menu.target_url;
};

var addCurrentRuleSetName = function addCurrentRuleSetName(targetUrl, currentRuleSetName) {
  if (!currentRuleSetName) return targetUrl;

  try {
    var url = new URL(targetUrl);

    if (!url.searchParams.get('current_rule_set_name')) {
      url.searchParams.set('current_rule_set_name', currentRuleSetName);
    }

    return url.toString();
  } catch (e) {
    console.error("failed to add rule set to ".concat(targetUrl), e);
    /*
      getting some undefined/cportal/something or other instead of a http://address
      here and there. breaks things for an advisor type. Reason: no next_url in the
      env.local file.
    */
  }
};

var dynamicSubdomainModules = ['engage', 'next'];
var domainPorts = [80, 443];

var withDynamicSubdomain = function withDynamicSubdomain(targetUrl, organization) {
  var url = new URL(targetUrl);
  var host = url.host;

  if (url.port && domainPorts.includes(url.port)) {
    host = "".concat(host, ":").concat(url.port);
  }

  var domain = host.concat(url.pathname);

  if (organization !== null && organization !== void 0 && organization.slug) {
    domain = "".concat(organization.slug, ".").concat(domain);
  }

  if (!organization) {
    // if no org, we are in Next Admin side of things.
    domain = "admin.".concat(domain);

    if (url !== null && url !== void 0 && url.search) {
      // for content list and layouts, we need to maintain this part of the url building.
      domain = domain.concat(url.search);
    }
  }

  return "".concat(url.protocol, "//").concat(domain);
};

var hasItems = function hasItems(target) {
  return Array.isArray(target) && (target === null || target === void 0 ? void 0 : target.length);
};

var handleTargetUrlArrayValidation = function handleTargetUrlArrayValidation(targets, permissionManager) {
  for (var index = 0; index < targets.length; index++) {
    var _targets$index = targets[index],
        url = _targets$index.url,
        permissions = _objectWithoutProperties(_targets$index, _excluded);

    if (!Object.keys(permissions).length || permissionManager.hasAccess([permissions])) {
      return url;
    }
  }
};

var withLocale = function withLocale(targetUrl, locale) {
  return targetUrl.replace('{{locale}}', locale);
};

var setUrlWithAvailableRuleSet = function setUrlWithAvailableRuleSet(ruleSets, ruleSetsRole, subMenu) {
  var _subMenu$target_url, _availableRuleSet$;

  var availableRuleSet = ruleSets === null || ruleSets === void 0 ? void 0 : ruleSets.filter(function (rule) {
    return ruleSetsRole === null || ruleSetsRole === void 0 ? void 0 : ruleSetsRole.includes(rule.name);
  });
  if (!availableRuleSet) return subMenu;
  var targetUrl = "".concat((_subMenu$target_url = subMenu.target_url) === null || _subMenu$target_url === void 0 ? void 0 : _subMenu$target_url.split('?')[0], "?current_rule_set_name=").concat((_availableRuleSet$ = availableRuleSet[0]) === null || _availableRuleSet$ === void 0 ? void 0 : _availableRuleSet$.name);
  return _objectSpread2(_objectSpread2({}, subMenu), {}, {
    target_url: targetUrl
  });
};

var _filterMenu = function filterMenu(args) {
  var currentRuleSetName = args.currentRuleSetName,
      isAdmin = args.isAdmin,
      isAppSwitcher = args.isAppSwitcher,
      locale = args.locale,
      menu = args.menu,
      organization = args.organization,
      user = args.user;
  var permissionManager = new PermissionManager(user, organization, currentRuleSetName);
  var hasAccess = true;

  if (!(user !== null && user !== void 0 && user.all_access)) {
    if (menu.all_access) {
      hasAccess = false;
    }

    if (hasAccess && menu.permissions) {
      hasAccess = permissionManager.hasAccess(menu.permissions, isAppSwitcher);

      if (hasItems(menu.target_url_array)) {
        menu.target_url = handleTargetUrlArrayValidation(menu.target_url_array, permissionManager);
      }
    }
  }

  if (hasAccess && menu) {
    var _menu$submenu_items, _menu$submenu_items2;

    if (((_menu$submenu_items = menu.submenu_items) === null || _menu$submenu_items === void 0 ? void 0 : _menu$submenu_items.length) > 0) {
      menu.submenu_items = menu.submenu_items.filter(function (item) {
        return _filterMenu(_objectSpread2(_objectSpread2({}, args), {}, {
          menu: item,
          isAppSwitcher: menu.id === 'app-switcher'
        }));
      });
    }

    if (menu.title) {
      menu.title = replaceUserName(user, menu.title);
    }

    if (menu.id === 'school-picker-button') {
      var _user$schools;

      menu.title = replaceSchoolName(user, menu.title);

      if ((user === null || user === void 0 || (_user$schools = user.schools) === null || _user$schools === void 0 ? void 0 : _user$schools.length) <= 1) {
        menu.target_url = replaceSchoolModalUrl(user, menu.target_url);
      }
    }

    if (menu.id === 'account_manager_email') {
      menu.sub_text = replaceAccountManagerEmailText(user, menu.sub_text);
      menu.target_url = replaceAccountManagerEmailUrl(user, menu.target_url);
    }

    if (menu !== null && menu !== void 0 && menu.help_url_post_processing) {
      menu.target_url = replaceHelpUrl(menu, organization, permissionManager.isAdvisor());
    }

    if (menu !== null && menu !== void 0 && menu.privacy_processing) {
      menu.target_url = replacePrivacyUrl(menu, organization);
    }

    if (menu.id === 'app-switcher') {
      menu.submenu_items = menu.submenu_items.map(function (subMenu) {
        return setUrlWithAvailableRuleSet(user.rule_sets, menuRoles[subMenu.id], subMenu);
      });
    }

    if (menu.id === 'my_profile') {
      var userIsManager = (user === null || user === void 0 ? void 0 : user.is_manager) || (user === null || user === void 0 ? void 0 : user['is_manager?']);
      menu.target_url = replaceMyProfileUrlContext(menu.target_url, userIsManager);
    }

    if (menu.id === 'localized') {
      menu.target_url = withLocale(menu.target_url, locale);
    }

    if (menu.target_url || ((_menu$submenu_items2 = menu.submenu_items) === null || _menu$submenu_items2 === void 0 ? void 0 : _menu$submenu_items2.length) > 0) {
      if (menu.target_url) {
        if (dynamicSubdomainModules.includes(menu.module)) {
          menu.target_url = withDynamicSubdomain(menu.target_url, organization);
        }

        if (menu.target_url === 'language_modal') {
          return menu;
        }

        if (!isAdmin) {
          menu.target_url = replaceOrgSlug(menu, organization, user);
          menu.target_url = addCurrentRuleSetName(menu.target_url, currentRuleSetName);
        }
      }

      return menu;
    }
  }

  return null;
};

var isCurrentMenuItemPermitted = function isCurrentMenuItemPermitted(menuData, currentUrl) {
  if (!menuData) {
    return false;
  }

  var items = menuData.submenu_items.concat(menuData.submenu_items.flatMap(function (item) {
    return item.submenu_items;
  }));
  return !!items.find(function (item) {
    var _item$target_url;

    return item === null || item === void 0 || (_item$target_url = item.target_url) === null || _item$target_url === void 0 ? void 0 : _item$target_url.includes(currentUrl);
  });
};

var withPermissionIndicator = function withPermissionIndicator(menuData, currentUrl) {
  return Object.assign({}, menuData, {
    current_menu_item_permitted: isCurrentMenuItemPermitted(menuData, currentUrl),
    permitted_whitelist: PERMITTED_WHITELIST
  });
};

var getMenuData = function getMenuData(args) {
  var slug = args.slug,
      environment = args.environment,
      currentUrl = args.currentUrl,
      props = _objectWithoutProperties(args, _excluded2);

  var menu = MENUS[slug] && MENUS[slug](environment);
  menu = _filterMenu(_objectSpread2(_objectSpread2({}, props), {}, {
    menu: menu
  }));

  if (slug === MENU_SLUG.customerAdminLeft) {
    menu = withPermissionIndicator(menu, currentUrl);
  }

  return menu;
};

export { PermissionManager, getMenuData };